import {
    color_colors_growth,
    color_colors_decline,
    color_colors_ocean,
    color_variants_decline_light_1,
    color_variants_ocean_light_1,
    highlight_green,
    color_variants_gold_light,
    color_colors_gold
} from '../colors';

export const ALL_TAGS_AND_CATEGORIES = [
    'ACTION_ATTEMPTED',
    'IMMEDIATE_TRANSFER',
    'OTHER',
    'NO_INTERACTION',
    'ACTION_COMPLETED',
    'TRANSFERRED',
    'UNSUPPORTED_CUSTOMER_REQUEST',
    'AI_ISSUE',
    'CUSTOMER_FRUSTRATION',
    'CUSTOMER_DELIGHT',
    'UPSELL_SUCCESSFUL',
    'UPSELL_ATTEMPTED',
    'APPOINTMENT_CANCELLED',
    'APPOINTMENT_RESCHEDULED',
    'VFY_PATIENT_HANGUP',
    'SPANISH',
    'UNSUPPORTED_APPOINTMENT_TYPE',
    'SCHEDULE_ATTEMPTED',
    'RESCHEDULE_ATTEMPTED',
    'CANCELLATION_ATTEMPTED',
    'CHECK_NEXT_APPOINTMENT'
];
// Tag constants - single source of truth
export const TAGS = {
    ACTION_COMPLETED: "ACTION_COMPLETED",
    TRANSFERRED: "TRANSFERRED",
    UNSUPPORTED_CUSTOMER_REQUEST: "UNSUPPORTED_CUSTOMER_REQUEST",
    AI_ISSUE: "AI_ISSUE",
    CUSTOMER_FRUSTRATION: "CUSTOMER_FRUSTRATION",
    CUSTOMER_DELIGHT: "CUSTOMER_DELIGHT",
    UPSELL_SUCCESSFUL: "UPSELL_SUCCESSFUL",
    UPSELL_ATTEMPTED: "UPSELL_ATTEMPTED",
    APPOINTMENT_CANCELLED: "APPOINTMENT_CANCELLED",
    APPOINTMENT_RESCHEDULED: "APPOINTMENT_RESCHEDULED",
    VFY_PATIENT_HANGUP: "VFY_PATIENT_HANGUP",
    SPANISH: "SPANISH",
    UNSUPPORTED_APPOINTMENT_TYPE: "UNSUPPORTED_APPOINTMENT_TYPE",
    SCHEDULE_ATTEMPTED: "SCHEDULE_ATTEMPTED",
    RESCHEDULE_ATTEMPTED: "RESCHEDULE_ATTEMPTED",
    CANCELLATION_ATTEMPTED: "CANCELLATION_ATTEMPTED",
    CHECK_NEXT_APPOINTMENT: "CHECK_NEXT_APPOINTMENT",
    CHECK_NEXT_APPOINTMENT_COMPLETE: "CHECK_NEXT_APPOINTMENT_COMPLETE",
    NEW_PATIENT: "NEW_PATIENT",
    PAYMENT_LINK_REFUSED: "PAYMENT_LINK_REFUSED",
    OUT_OF_STOCK_ORDERED: "OUT_OF_STOCK_ORDERED",
    ABANDONED_ON_PRICE: "ABANDONED_ON_PRICE",
    ABANDONED_ON_NAME: "ABANDONED_ON_NAME",
    CALL_DURING_OFF_HOURS: "CALL_DURING_OFF_HOURS"
} as const;

export const ALL_CATEGORIES = [
    "ACTION_ATTEMPTED",
    "QUESTION_ANSWERED",
    "OTHER",
    "IMMEDIATE_TRANSFER",
    "NO_INTERACTION"
]

export const HEALTHCARE_TAGS = [
    "ACTION_COMPLETED",
    "AI_ISSUE",
    "TRANSFERRED",
    "UNSUPPORTED_CUSTOMER_REQUEST",
    "CUSTOMER_DELIGHT",
    "CUSTOMER_FRUSTRATION",
    "APPOINTMENT_RESCHEDULED",
    "APPOINTMENT_CANCELLED",
    "UNSUPPORTED_APPOINTMENT_TYPE",
    "SCHEDULE_ATTEMPTED",
    "RESCHEDULE_ATTEMPTED",
    "CANCELLATION_ATTEMPTED",
    "CHECK_NEXT_APPOINTMENT",
    "CHECK_NEXT_APPOINTMENT_COMPLETE",
    "NEW_PATIENT",
    "SPANISH",
    "CALL_DURING_OFF_HOURS"
];

export const FOOD_ORDERING_TAGS = [
    "ACTION_COMPLETED",
    "AI_ISSUE",
    "TRANSFERRED",
    "UNSUPPORTED_CUSTOMER_REQUEST",
    "CUSTOMER_DELIGHT",
    "CUSTOMER_FRUSTRATION",
    "UPSELL_ATTEMPTED",
    "UPSELL_SUCCESSFUL",
    "SPANISH",
    "PAYMENT_LINK_REFUSED",
    "OUT_OF_STOCK_ORDERED",
    "ABANDONED_ON_PRICE",
    "ABANDONED_ON_NAME",
    "CALL_DURING_OFF_HOURS"
]

// Tag categorization for easy maintenance
export const POSITIVE_TAGS = [TAGS.ACTION_COMPLETED, TAGS.CUSTOMER_DELIGHT, TAGS.UPSELL_SUCCESSFUL, TAGS.APPOINTMENT_RESCHEDULED, TAGS.APPOINTMENT_CANCELLED, TAGS.NEW_PATIENT, TAGS.CHECK_NEXT_APPOINTMENT_COMPLETE] as const;
export const NEGATIVE_TAGS = [TAGS.AI_ISSUE, TAGS.CUSTOMER_FRUSTRATION] as const;
export const NEUTRAL_TAGS = [TAGS.TRANSFERRED, TAGS.UPSELL_ATTEMPTED, TAGS.SPANISH, TAGS.UNSUPPORTED_APPOINTMENT_TYPE, TAGS.SCHEDULE_ATTEMPTED, TAGS.RESCHEDULE_ATTEMPTED, TAGS.CANCELLATION_ATTEMPTED, TAGS.CHECK_NEXT_APPOINTMENT] as const;
export const CAUTION_TAGS = [TAGS.UNSUPPORTED_CUSTOMER_REQUEST, TAGS.PAYMENT_LINK_REFUSED, TAGS.OUT_OF_STOCK_ORDERED, TAGS.ABANDONED_ON_PRICE, TAGS.ABANDONED_ON_NAME] as const;

// All tags in priority order (positive, neutral, negative)
export const ALL_TAGS = [...POSITIVE_TAGS, ...NEUTRAL_TAGS, ...NEGATIVE_TAGS, ...CAUTION_TAGS];

export const getCategoryName = (category: string, featureFlag?: string) => {

    // CallCategories: "NO_INTERACTION" | "IMMEDIATE_TRANSFER" | "ACTION_ATTEMPTED" | "OTHER";
    if (!featureFlag) {
        return category; // Fallback to the original category if featureFlag is not provided
    };

    if (featureFlag === "FOOD_ORDERING") {
        if (category === "NO_INTERACTION") {
            return "No Interaction";
        } else if (category === "IMMEDIATE_TRANSFER") {
            return 'Immediate Transfer';
        } else if (category === "QUESTION_ANSWERED") {
            return 'Question Answered';
        } else if (category === "ACTION_ATTEMPTED") {
            return 'Order Attempts';
        } else if (category === "OTHER") {
            return 'Miscellaneous';
        } else {
            return category; // Fallback to the original category if not matched
        }
    } else if (featureFlag === "HEALTHCARE") {
        if (category === "NO_INTERACTION") {
            return "No Interaction";
        } else if (category === "IMMEDIATE_TRANSFER") {
            return 'Immediate Transfers';
        } else if (category === "QUESTION_ANSWERED") {
            return 'Question Answered';
        } else if (category === "ACTION_ATTEMPTED") {
            return 'Appointment Action Attempts';
        } else if (category === "OTHER") {
            return 'Miscellaneous';
        } else {
            return category;
        }
    } else {
        return category; // Fallback to the original category if featureFlag doesn't match
    }
};

export const getTagName = (tag: string, featureFlag?: string) => {

    if (!featureFlag) {
        return tag; // Fallback to the original tag if featureFlag is not provided
    }

    if (featureFlag === "FOOD_ORDERING") {
        switch (tag) {
            case TAGS.ACTION_COMPLETED:
                return "Order Placed";
            case TAGS.TRANSFERRED:
                return "Transferred";
            case TAGS.UNSUPPORTED_CUSTOMER_REQUEST:
                return "Unsupported Customer Request";
            case TAGS.AI_ISSUE:
                return "AI Issue";
            case TAGS.CUSTOMER_FRUSTRATION:
                return "Customer Frustration";
            case TAGS.CUSTOMER_DELIGHT:
                return "Customer Delight";
            case TAGS.UPSELL_SUCCESSFUL:
                return "Upsell Successful";
            case TAGS.UPSELL_ATTEMPTED:
                return "Upsell Attempted";
            case TAGS.PAYMENT_LINK_REFUSED:
                return "Payment Link Refused";
            case TAGS.OUT_OF_STOCK_ORDERED:
                return "Out of Stock Item Ordered";
            case TAGS.SPANISH:
                return "Spanish";
            case TAGS.ABANDONED_ON_PRICE:
                return "Abandoned on Price";
            case TAGS.ABANDONED_ON_NAME:
                return "Abandoned on Name";
            case TAGS.CALL_DURING_OFF_HOURS:
                return "Call During Off Hours";
            default:
                return tag; // Fallback to the original tag if not matched
        }
    } else if (featureFlag === "HEALTHCARE") {
        switch (tag) {
            case TAGS.ACTION_COMPLETED:
                return "Appointment Scheduled";
            case TAGS.TRANSFERRED:
                return "Transferred";
            case TAGS.UNSUPPORTED_CUSTOMER_REQUEST:
                return "Unsupported Customer Request";
            case TAGS.AI_ISSUE:
                return "AI Issue";
            case TAGS.CUSTOMER_FRUSTRATION:
                return "Customer Frustration";
            case TAGS.CUSTOMER_DELIGHT:
                return "Customer Delight";
            case TAGS.UPSELL_SUCCESSFUL:
                return "Upsell Successful";
            case TAGS.UPSELL_ATTEMPTED:
                return "Upsell Attempted";
            case TAGS.APPOINTMENT_RESCHEDULED:
                return "Appointment Rescheduled";
            case TAGS.APPOINTMENT_CANCELLED:
                return "Appointment Cancelled";
            case TAGS.VFY_PATIENT_HANGUP:
                return "Patient Hangup";
            case TAGS.SPANISH:
                return "Spanish";
            case TAGS.UNSUPPORTED_APPOINTMENT_TYPE:
                return "Unsupported Appointment Type";
            case TAGS.SCHEDULE_ATTEMPTED:
                return "Schedule Attempted";
            case TAGS.RESCHEDULE_ATTEMPTED:
                return "Reschedule Attempted";
            case TAGS.CANCELLATION_ATTEMPTED:
                return "Cancellation Attempted";
            case TAGS.CHECK_NEXT_APPOINTMENT:
                return "Checked Next Appointment";
            case TAGS.NEW_PATIENT:
                return "New Patient";
            case TAGS.CHECK_NEXT_APPOINTMENT_COMPLETE:
                return "Check Next Appointment Successful"
            case TAGS.CALL_DURING_OFF_HOURS:
                return "Call During Off Hours";
            default:
                return tag;
        }
    } else {
        return tag; // Fallback to the original tag if featureFlag doesn't match
    }
};

const neutralTagColors = {
    background: color_variants_ocean_light_1,
    color: color_colors_ocean,
    border: color_colors_ocean
};

const positiveTagColors = {
    background: highlight_green,
    color: color_colors_growth,
    border: color_colors_growth
};

const negativeTagColors = {
    background: color_variants_decline_light_1,
    color: color_colors_decline,
    border: color_colors_decline
};

const cautionTagColors = {
    background: color_variants_gold_light,
    color: color_colors_gold,
    border: color_colors_gold
};

export const getTagPriority = (tag: string): number => {
    let total = 0;
    // Returns priority: 1 = positive (highest), 2 = neutral, 3 = negative (lowest)
    if (POSITIVE_TAGS.includes(tag as any)) {
        return 1; // positive tags
    } else if (NEGATIVE_TAGS.includes(tag as any)) {
        return 4; // negative tags
    } else if (CAUTION_TAGS.includes(tag as any)) {
        return 3; // caution tags
    } else {
        return 2; // neutral tags (TRANSFERRED, UNSUPPORTED_CUSTOMER_REQUEST, UPSELL_ATTEMPTED, etc.)
    }
};

export const sortTagsByPriority = (tags: string[], featureFlag?: string): string[] => {
    return [...tags].sort((a, b) => {
        const priorityA = getTagPriority(a);
        const priorityB = getTagPriority(b);

        // If same priority, sort alphabetically by display name
        if (priorityA === priorityB) {
            const displayNameA = getTagName(a, featureFlag) || a;
            const displayNameB = getTagName(b, featureFlag) || b;
            return displayNameA.localeCompare(displayNameB);
        }

        return priorityA - priorityB;
    });
};

export const getTagColors = (tag: string) => {
    if (POSITIVE_TAGS.includes(tag as any)) {
        return positiveTagColors;
    } else if (CAUTION_TAGS.includes(tag as any)) {
        return cautionTagColors;
    } else if (NEGATIVE_TAGS.includes(tag as any)) {
        return negativeTagColors;
    } else {
        return neutralTagColors; // Default for neutral tags and unknown tags
    }
};

export const getTagCombinationPriority = (tags: string[] | undefined): number => {
    let total = 0;
    if (!tags || tags.length === 0) {
        return 100;
    }
    for (const tag of tags || []) {
        if (POSITIVE_TAGS.includes(tag as any)) {
            total++; // positive tags
        }
        if (NEGATIVE_TAGS.includes(tag as any)) {
            total += 4;

        }
        if (CAUTION_TAGS.includes(tag as any)) {
            total += 3;
        }
        {
            total += 2;
        }
    }
    return total;
};